import { useEffect } from "react";

/**
 * Stop the page scrolling behind something laid over it (drawer, modal,
 * search palette) WITHOUT moving it.
 *
 * ⚠️ LOCK <html>, NEVER <body>. The overflow guardrail puts `overflow-x: clip`
 * on <html>, and a root that is not `overflow: visible` no longer hands
 * <body>'s overflow up to the window. So `body { overflow: hidden }` turned
 * <body> (height: 100%) into its own clipped box: the document shrank to one
 * screen, the window's scroll position reset to 0 and the list behind the
 * drawer jumped to the top -- then jumped back on close. Measured 2026-10-15:
 * the last Accounts card went from 155px to 749px down the screen on open.
 * <html> is the element that actually scrolls, and hiding its overflow keeps
 * the scroll position where it is.
 *
 * Counted, because these nest: a modal opened from inside the drawer must
 * not unlock the page when it closes while the drawer is still open.
 */
let locks = 0;
let previousOverflow = "";
let previousPad = "";

function lock() {
  if (locks === 0) {
    const root = document.documentElement;
    previousOverflow = root.style.overflow;
    previousPad = root.style.paddingRight;
    // ⚠️ Compensate for the scrollbar's width (Jonathan, 2026-10-21: "the whole
    // back page blinks ... like a quick refresh" on open AND close). Hiding the
    // overflow removes the vertical scrollbar, which widens the viewport by the
    // scrollbar's width and reflows every element — a horizontal jump that reads
    // as a blink. Measured BEFORE hiding (while the bar is still there) and
    // added back as padding, so the content does not move at all.
    const scrollbar = window.innerWidth - root.clientWidth;
    root.style.overflow = "hidden";
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
  }
  locks += 1;
}

function unlock() {
  locks = Math.max(0, locks - 1);
  if (locks === 0) {
    const root = document.documentElement;
    root.style.overflow = previousOverflow;
    root.style.paddingRight = previousPad;
  }
}

/** Holds the lock for as long as the calling component is mounted. */
export function useScrollLock(active = true): void {
  useEffect(() => {
    if (!active) return undefined;
    lock();
    return unlock;
  }, [active]);
}
